import { useEffect, useState, useMemo } from "react";
import { api, euro, formatDate, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, PaperPlaneTilt, Receipt, PencilSimple, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BookingActionMenu from "@/components/BookingActionMenu";
import FormStatusIcons from "@/components/FormStatusIcons";
import FormSubmissionModal from "@/components/FormSubmissionModal";
import BookingExtrasDialog from "@/components/BookingExtrasDialog";
import PdfViewerDialog from "@/components/PdfViewerDialog";
import { GuestAccessPanel } from "@/components/GuestAccessPanel";

const STATUS_COLORS = {
  "Angefragt": "bg-amber-100 text-amber-800",
  "Bestätigt": "bg-emerald-100 text-emerald-800",
  "Anreise bald": "bg-blue-100 text-blue-800",
  "Aktuell im Haus": "bg-primary/15 text-primary",
  "Abgereist": "bg-slate-100 text-slate-700",
  "Storniert": "bg-red-100 text-red-800",
  "Privat gesperrt": "bg-slate-200 text-slate-700",
  "Prüfung erforderlich": "bg-orange-100 text-orange-800 animate-pulse",
};

// Buchungsquelle → farbliche Pill
const SOURCE_COLORS = {
  "MSB":         "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200",
  "Booking.com": "bg-blue-100 text-blue-800 ring-1 ring-blue-200",
  "Airbnb":      "bg-red-100 text-red-800 ring-1 ring-red-200",
  "Website":     "bg-amber-100 text-amber-800 ring-1 ring-amber-200",
};
const canonSource = (s) => {
  const x = (s || "").toLowerCase();
  if (x.includes("booking")) return "Booking.com";
  if (x.includes("airbnb"))  return "Airbnb";
  if (x.includes("website")) return "Website";
  return "MSB";
};

const PAY_COLORS = {
  "bezahlt": "bg-emerald-100 text-emerald-800",
  "Vollständig bezahlt": "bg-emerald-100 text-emerald-800",
  "Offen": "bg-red-100 text-red-800",
  "offen": "bg-red-100 text-red-800",
  "Offen Booking": "bg-orange-100 text-orange-800 ring-1 ring-orange-300",
  "Angezahlt (50%)": "bg-amber-100 text-amber-800",
  "Anzahlung": "bg-amber-100 text-amber-800",
  "über Booking.com bezahlt": "bg-blue-100 text-blue-800",
  "über Airbnb bezahlt": "bg-blue-100 text-blue-800",
  "Auszahlung erwartet (Booking)": "bg-sky-100 text-sky-800",
  "Auszahlung erwartet (Airbnb)": "bg-sky-100 text-sky-800",
  "Kostenübernahme": "bg-slate-100 text-slate-700",
  "Privat gesperrt (kein Umsatz)": "bg-slate-200 text-slate-700",
};

const PAY_OPTIONS = [
  "Offen",
  "Offen Booking",
  "Angezahlt (50%)",
  "Vollständig bezahlt",
  "über Airbnb bezahlt",
  "Auszahlung erwartet (Booking)",
  "über Booking.com bezahlt",
  "Kostenübernahme",
];

export default function Bookings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null); // Buchung für Full-Edit
  const [commEdit, setCommEdit] = useState(null); // Booking für Provisionsabgleich
  const [prefill, setPrefill] = useState(null); // { room_id, check_in, nights }
  const [formsStatus, setFormsStatus] = useState({}); // {booking_id: {checkin, breakfast, dinner}}
  const [formModal, setFormModal] = useState(null); // {bookingId, formType}
  const [extrasFor, setExtrasFor] = useState(null); // booking for extras dialog
  const [invoicePicker, setInvoicePicker] = useState(null); // { booking, extras }
  const [pdfPreview, setPdfPreview] = useState(null); // { url, title, filename }
  // Filter + Live-Suche
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fSource, setFSource] = useState("");
  const [fPayment, setFPayment] = useState("");
  const [fTime, setFTime] = useState("");           // "upcoming"|"current"|"past"|""
  const [fRoomId, setFRoomId] = useState("");
  const [scope, setScope] = useState("current");    // "current" | "all"
  const [loading, setLoading] = useState(false);

  const loadFormsStatus = (bookings) => {
    const ids = bookings.map(b => b.id).filter(Boolean);
    if (!ids.length) return;
    api.get("/forms/bookings-status", { params: { ids: ids.join(",") } })
       .then(r => setFormsStatus(r.data))
       .catch(() => {});
  };
  const markManual = async (bookingId, form_type, done) => {
    try {
      await api.post(`/bookings/${bookingId}/manual-form-mark`, { form_type, done });
      setFormsStatus(prev => ({
        ...prev,
        [bookingId]: { ...(prev[bookingId] || {}), [`manual_${form_type}`]: done }
      }));
    } catch (e) {
      window.alert("Konnte Markierung nicht speichern. Bitte erneut versuchen.");
    }
  };
  const load = () => {
    setLoading(true);
    return api.get("/bookings", { params: { scope } })
      .then(r => { setRows(r.data); loadFormsStatus(r.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/rooms").then(r => setRooms(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Buchungen mit gemeinsamer booking_group_id werden als 1 Zeile angezeigt.
  // Filter + Live-Suche werden VOR der Gruppierung angewendet, damit die Anzeige konsistent bleibt.
  const filteredRows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const today = new Date().toISOString().slice(0, 10);
    return rows.filter(b => {
      // Textsuche
      if (s) {
        const bag = [
          b.guest?.first_name, b.guest?.last_name, b.guest?.company,
          b.guest?.email, b.guest?.phone, b.room?.name,
          b.invoice_number, b.source, b.internal_note,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!bag.includes(s)) return false;
      }
      if (fStatus && b.status !== fStatus) return false;
      if (fSource && canonSource(b.source) !== fSource) return false;
      if (fPayment && b.payment_status !== fPayment) return false;
      if (fRoomId && b.room_id !== fRoomId) return false;
      if (fTime === "upcoming"  && !(b.check_in > today)) return false;
      if (fTime === "current"   && !(b.check_in <= today && b.check_out > today)) return false;
      if (fTime === "past"      && !(b.check_out <= today)) return false;
      if (fTime === "unpaid"    && b.payment_status !== "Offen" && b.payment_status !== "offen" && b.payment_status !== "Offen Booking") return false;
      return true;
    });
  }, [rows, q, fStatus, fSource, fPayment, fRoomId, fTime]);

  const groupedRows = useMemo(() => {
    const groups = new Map();
    const out = [];
    for (const b of filteredRows) {
      const gid = b.booking_group_id;
      if (!gid) { out.push(b); continue; }
      if (!groups.has(gid)) {
        const wrapper = {
          ...b,
          _group_id: gid,
          _group_members: [b],
          _room_names: [b.room?.name].filter(Boolean),
          _gross_total: Number(b.gross || 0),
          _extras_inv_total: Number(b.extras_invoiceable_total || 0),
          _extras_tip_total: Number(b.extras_tip_total || 0),
          _commission_total: Number(b.commission_amount || 0),
          _net_total: Number(b.net_profit ?? b.gross ?? 0),
        };
        groups.set(gid, wrapper);
        out.push(wrapper);
      } else {
        const g = groups.get(gid);
        g._group_members.push(b);
        if (b.room?.name && !g._room_names.includes(b.room.name)) g._room_names.push(b.room.name);
        g._gross_total += Number(b.gross || 0);
        g._extras_inv_total += Number(b.extras_invoiceable_total || 0);
        g._extras_tip_total += Number(b.extras_tip_total || 0);
        g._commission_total += Number(b.commission_amount || 0);
        g._net_total += Number(b.net_profit ?? b.gross ?? 0);
      }
    }
    return out;
  }, [filteredRows]);

  const activeFilterCount =
    (q ? 1 : 0) + (fStatus ? 1 : 0) + (fSource ? 1 : 0) +
    (fPayment ? 1 : 0) + (fTime ? 1 : 0) + (fRoomId ? 1 : 0);
  const resetFilters = () => {
    setQ(""); setFStatus(""); setFSource(""); setFPayment(""); setFTime(""); setFRoomId("");
  };

  // URL-Deep-Link (einmalig beim Mount): ?new=1&rooms=id1,id2&check_in=YYYY-MM-DD&check_out=YYYY-MM-DD&guests=N
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("new") === "1") {
      const roomsCsv = p.get("rooms") || p.get("room") || "";
      const roomIds = roomsCsv.split(",").map(s => s.trim()).filter(Boolean);
      const checkIn = p.get("check_in") || "";
      let checkOut = p.get("check_out") || "";
      const nightsQ = Math.max(1, Number(p.get("nights")) || 0);
      if (!checkOut && checkIn && nightsQ > 0) {
        const dt = new Date(checkIn); dt.setDate(dt.getDate() + nightsQ);
        checkOut = dt.toISOString().slice(0, 10);
      }
      const nightsFinal = (checkIn && checkOut)
        ? Math.max(1, Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000))
        : 1;
      setPrefill({
        room_id:   roomIds[0] || "",
        room_ids:  roomIds,
        check_in:  checkIn,
        check_out: checkOut,
        nights:    nightsFinal,
        guests:    Math.max(1, Number(p.get("guests")) || 1),
      });
      setOpen(true);
      // URL wieder säubern (History-API, kein Reload)
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const genInvoice = async (booking_id) => {
    const b = rows.find(x => x.id === booking_id);
    const invoiceable = (b?.extras || []).filter(e => e.kind !== "tip");
    // Immer den Optionen-Dialog zeigen (Rechnungsempfänger + Zahlungsart + optional Extras)
    setInvoicePicker({ booking: b, extras: invoiceable, selectedIds: invoiceable.map(e => e.id) });
  };
  const createInvoice = async (booking_id, include_extra_ids, opts = {}) => {
    try {
      const payload = { booking_id, ...opts };
      if (include_extra_ids !== null) payload.include_extra_ids = include_extra_ids;
      const { data } = await api.post("/invoices/generate", payload);
      toast.success(`Rechnung ${data.number} erstellt`);
      setPdfPreview({ url: `${API}/invoices/${data.id}/pdf?v=${Date.now()}`, title: `Rechnung ${data.number}`, filename: `Rechnung_${data.number}.pdf` });
      setInvoicePicker(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Erstellen"); }
  };

  const sendCheckin = async (booking_id) => {
    try {
      await api.post(`/bookings/${booking_id}/send-checkin-form-reminder`);
      toast.success("Check-in Link versendet");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Buchungen</h1>
          <p className="text-muted-foreground mt-1">
            {activeFilterCount > 0
              ? <>{groupedRows.length} von {rows.length} Buchungen · <button onClick={resetFilters} className="underline hover:text-primary" data-testid="bookings-filter-reset">Filter zurücksetzen</button></>
              : <>{rows.length} Buchungen insgesamt</>}
          </p>
        </div>
        {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-booking-btn" className="rounded-xl bg-primary"><Plus size={16} weight="bold"/> <span className="ml-1.5">Neue Buchung</span></Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="quick-booking-dialog">
            <DialogHeader>
              <DialogTitle>Neue Buchung · Blitz-Eingabe</DialogTitle>
            </DialogHeader>
            <QuickBookingForm rooms={rooms} prefill={prefill}
                              onSaved={()=>{ setOpen(false); setPrefill(null); load(); }}
                              onCancel={()=>{ setOpen(false); setPrefill(null); }}/>
          </DialogContent>
        </Dialog>)}
      </div>

      {/* Filter- & Suchleiste */}
      <div className="msb-card p-3 space-y-3">
        {/* 📅 Scope-Toggle: Aktuell vs. Alle Buchungen */}
        <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-slate-200">
          <button type="button" onClick={() => setScope("current")}
                  data-testid="scope-current-btn"
                  className={`h-9 px-4 rounded-full border font-semibold text-xs transition ${
                    scope === "current"
                      ? "bg-emerald-600 text-white border-emerald-600 shadow"
                      : "bg-white text-slate-700 border-slate-300 hover:border-emerald-500"
                  }`}>
            📅 Aktuell (letzte 2 Monate + Zukunft + Unbezahlt)
          </button>
          <button type="button" onClick={() => setScope("all")}
                  data-testid="scope-all-btn"
                  className={`h-9 px-4 rounded-full border font-semibold text-xs transition ${
                    scope === "all"
                      ? "bg-slate-700 text-white border-slate-700 shadow"
                      : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                  }`}>
            📚 Alle Buchungen (Archiv)
          </button>
          <div className="text-[11px] text-muted-foreground ml-auto flex items-center gap-2">
            {loading && <span className="inline-flex items-center gap-1"><span className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin"/> Lade…</span>}
            {!loading && (
              <span className="font-medium">
                <b className="text-foreground">{rows.length}</b> Buchung{rows.length === 1 ? "" : "en"}
                {scope === "current" && <span className="text-emerald-700"> · schnelle Ansicht</span>}
                {scope === "all"     && <span className="text-slate-600"> · komplettes Archiv</span>}
              </span>
            )}
          </div>
        </div>
        <div className="relative">
          <Input
            placeholder="Suchen nach Name, Firma, E-Mail, Telefon, Zimmer, Rechnungsnr. …"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="pl-10 pr-10 rounded-xl h-11 text-sm"
            data-testid="bookings-search"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
          {q && (
            <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted-foreground hover:text-primary" data-testid="bookings-search-clear">×</button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {[
            { v: "current",  label: "Aktuell im Haus" },
            { v: "upcoming", label: "Anreise steht an" },
            { v: "past",     label: "Bereits abgereist" },
            { v: "unpaid",   label: "Offene Zahlung" },
          ].map(o => (
            <button key={o.v} type="button"
                    onClick={() => setFTime(fTime === o.v ? "" : o.v)}
                    className={`h-8 px-3 rounded-full border font-semibold transition ${fTime===o.v ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-slate-300 hover:border-primary/60"}`}
                    data-testid={`bookings-quick-${o.v}`}>
              {o.label}
            </button>
          ))}
          <div className="w-px h-6 bg-border mx-1"/>
          <select value={fStatus} onChange={e => setFStatus(e.target.value)}
                  className="h-8 rounded-lg border border-slate-300 bg-white px-2" data-testid="bookings-filter-status">
            <option value="">Alle Status</option>
            {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fSource} onChange={e => setFSource(e.target.value)}
                  className="h-8 rounded-lg border border-slate-300 bg-white px-2" data-testid="bookings-filter-source">
            <option value="">Alle Quellen</option>
            {Object.keys(SOURCE_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fPayment} onChange={e => setFPayment(e.target.value)}
                  className="h-8 rounded-lg border border-slate-300 bg-white px-2" data-testid="bookings-filter-payment">
            <option value="">Alle Zahlungen</option>
            {PAY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fRoomId} onChange={e => setFRoomId(e.target.value)}
                  className="h-8 rounded-lg border border-slate-300 bg-white px-2" data-testid="bookings-filter-room">
            <option value="">Alle Zimmer</option>
            {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          {activeFilterCount > 0 && (
            <button onClick={resetFilters} className="h-8 px-3 rounded-lg text-xs font-semibold text-rose-600 hover:bg-rose-50" data-testid="bookings-clear-all">
              Alle Filter löschen
            </button>
          )}
        </div>
      </div>

      <div className="msb-card p-0 overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-muted/60">
            <tr className="text-xs uppercase tracking-widest text-muted-foreground">
              <th className="text-left p-4">Gast</th>
              <th className="text-left p-4">Zimmer</th>
              <th className="text-left p-4">Zeitraum</th>
              <th className="text-left p-4">Quelle</th>
              <th className="text-left p-4">Formulare</th>
              <th className="text-left p-4">Betrag</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Zahlung</th>
              <th className="text-right p-4">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {groupedRows.map(b => {
              const isGroup = !!b._group_id;
              const roomLabel = isGroup ? b._room_names.join(" + ") : (b.room?.name || "");
              const grossDisplay = isGroup ? b._gross_total : b.gross;
              const extrasInvDisplay = isGroup ? b._extras_inv_total : (b.extras_invoiceable_total || 0);
              const extrasTipDisplay = isGroup ? b._extras_tip_total : (b.extras_tip_total || 0);
              const commDisplay = isGroup ? b._commission_total : b.commission_amount;
              const netDisplay = isGroup ? b._net_total : b.net_profit;
              return (
              <tr key={b._group_id || b.id}
                  className="border-t border-border hover:bg-muted/40 cursor-pointer"
                  onClick={(e) => {
                    // Nicht auslösen, wenn ein Button/Link innerhalb der Zeile geklickt wurde
                    if (e.target.closest("button, a, input, select, textarea, [role='menu']")) return;
                    setEdit(b);
                  }}
                  data-testid={`booking-row-${b.id}`}>
                <td className="p-4">
                  <div className="font-medium">{b.guest?.first_name} {b.guest?.last_name}</div>
                  {b.guest?.company && <div className="text-xs text-muted-foreground">{b.guest.company}</div>}
                </td>
                <td className="p-4 text-sm">
                  {roomLabel}
                  {isGroup && (
                    <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mt-0.5">
                      {b._group_members.length} Zimmer · 1 Buchung
                    </div>
                  )}
                </td>
                <td className="p-4 text-sm">
                  <div>{formatDate(b.check_in)} → {formatDate(b.check_out)}</div>
                  <div className="text-xs text-muted-foreground">{b.nights} Nächte</div>
                </td>
                <td className="p-4">
                  {(() => {
                    const src = canonSource(b.source);
                    return (
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${SOURCE_COLORS[src]}`}
                            data-testid={`booking-source-${b.id}`}>
                        {src}
                      </span>
                    );
                  })()}
                </td>
                <td className="p-4">
                  <FormStatusIcons
                    status={formsStatus[b.id]}
                    onOpen={(ft) => setFormModal({ bookingId: b.id, formType: ft })}
                    onManualToggle={(ft, done) => markManual(b.id, ft, done)}
                    testidPrefix={`booking-form-${b.id}`}
                  />
                </td>
                <td className="p-4">
                  {b.is_block ? (
                    <span className="text-xs text-muted-foreground italic">Privat gesperrt</span>
                  ) : grossDisplay == null ? (
                    <div className="text-[11px] text-muted-foreground">
                      Betrag nicht erfasst
                      {isAdmin && (
                        <button className="ml-1 underline text-primary"
                                onClick={()=>setEdit(b)}
                                data-testid={`add-amount-${b.id}`}>eintragen</button>
                      )}
                    </div>
                  ) : b.needs_review ? (
                    <div className="text-xs text-orange-800">
                      Betrag/Name aus Beleg ergänzen
                    </div>
                  ) : (
                    <>
                      <div className="font-semibold">{euro(grossDisplay)}</div>
                      {isGroup && (
                        <div className="text-[10px] text-muted-foreground">
                          gesplittet: {b._group_members.map(m => euro(m.gross || 0)).join(" + ")}
                        </div>
                      )}
                      {extrasInvDisplay > 0 && (
                        <div className="text-[11px] text-violet-700 font-medium">+ Extras {euro(extrasInvDisplay)}</div>
                      )}
                      {extrasTipDisplay > 0 && (
                        <div className="text-[10px] text-emerald-700">Trinkgeld {euro(extrasTipDisplay)}</div>
                      )}
                      {b.commission_source === "manuell erfasst" && commDisplay != null ? (
                        <div className="text-[11px] text-muted-foreground">
                          Basis {euro(b.commissionable_amount ?? 0)} · − {euro(commDisplay)}
                          <div className="text-primary font-medium">Netto {euro(netDisplay)}</div>
                        </div>
                      ) : (b.commission_source || "").startsWith("auto Booking.com") && commDisplay != null ? (
                        <div className="text-[11px] text-red-700" data-testid={`comm-auto-${b.id}`}>
                          − {euro(commDisplay)} <span className="text-muted-foreground">(auto 13,4 %)</span>
                          <div className="text-emerald-700 font-semibold">Netto {euro(netDisplay)}</div>
                          {isAdmin && (
                            <button className="underline text-muted-foreground hover:text-primary text-[10px]"
                                    onClick={()=>setCommEdit(b)}
                                    data-testid={`edit-commission-${b.id}`}>Beleg-Werte übersteuern</button>
                          )}
                        </div>
                      ) : b.commission_source === "keine Provision" ? (
                        <div className="text-[11px] text-primary font-medium">Netto {euro(grossDisplay)}</div>
                      ) : (
                        <div className="text-[11px] text-amber-700">
                          Provision nicht erfasst
                          {isAdmin && (
                            <button className="ml-1 underline"
                                    onClick={()=>setCommEdit(b)}
                                    data-testid={`add-commission-${b.id}`}>erfassen</button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </td>
                <td className="p-4"><span className={`status-pill ${STATUS_COLORS[b.status]||"bg-slate-100"}`}>{b.status}</span></td>
                <td className="p-4"><span className={`status-pill ${PAY_COLORS[b.payment_status]||"bg-slate-100"}`}>{b.payment_status}</span></td>
                <td className="p-4 text-right whitespace-nowrap">
                  <BookingActionMenu
                    booking={b}
                    isAdmin={isAdmin}
                    onEdit={setEdit}
                    onExtras={() => setExtrasFor(b)}
                    onGenInvoice={genInvoice}/>
                </td>
              </tr>
              );
            })}
            {groupedRows.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">
              {activeFilterCount > 0
                ? <>Keine Buchungen entsprechen den Filtern. <button onClick={resetFilters} className="underline text-primary">Filter zurücksetzen</button></>
                : "Keine Buchungen."}
            </td></tr>}
          </tbody>
        </table>
      </div>

      {edit && <BookingEditDialog booking={edit} onClose={()=>setEdit(null)} onSaved={()=>{setEdit(null); load();}}/>}
      {commEdit && <CommissionDialog booking={commEdit} onClose={()=>setCommEdit(null)} onSaved={()=>{setCommEdit(null); load();}}/>}
      <FormSubmissionModal
        bookingId={formModal?.bookingId}
        formType={formModal?.formType}
        open={formModal !== null}
        onClose={() => setFormModal(null)}
      />
      {extrasFor && (
        <BookingExtrasDialog
          open={!!extrasFor}
          booking={extrasFor}
          onClose={() => setExtrasFor(null)}
          onChanged={load}
        />
      )}
      {invoicePicker && (
        <InvoiceOptionsDialog
          booking={invoicePicker.booking}
          extras={invoicePicker.extras}
          initialSelected={invoicePicker.selectedIds}
          onCancel={() => setInvoicePicker(null)}
          onConfirm={(ids, opts) => createInvoice(invoicePicker.booking.id, ids, opts)}
        />
      )}
      <PdfViewerDialog
        open={!!pdfPreview}
        onClose={() => setPdfPreview(null)}
        url={pdfPreview?.url}
        title={pdfPreview?.title}
        filename={pdfPreview?.filename}
      />
    </div>
  );
}

function InvoiceOptionsDialog({ booking, extras, initialSelected, onCancel, onConfirm }) {
  const guest = booking?.guest || {};
  const room = booking?.room || {};
  const [sel, setSel] = useState(new Set(initialSelected));
  const toggle = (id) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); setSel(n); };
  // Rechnungsempfänger — Vorbelegung: gespeicherte Rechnungsadresse am Gast > Gast-Anschrift
  const guestFullName = `${guest.first_name || ""} ${guest.last_name || ""}`.trim();
  const guestAddress = [guest.street, `${guest.postal_code || ""} ${guest.city || ""}`.trim(), guest.country]
    .filter(x => x && x.trim()).join(", ");
  const [billingName, setBillingName] = useState(guest.billing_name || guestFullName);
  const [billingCompany, setBillingCompany] = useState(guest.billing_company || guest.company || "");
  const [billingAttention, setBillingAttention] = useState(guest.billing_attention || "");
  const [billingAddress, setBillingAddress] = useState(guest.billing_address || guestAddress);
  // Zahlungsart
  const src = (booking?.source || "").toLowerCase();
  const defaultMode = src.includes("booking") ? "booking" : src.includes("airbnb") ? "airbnb" : "transfer";
  const [payMode, setPayMode] = useState(defaultMode);
  // Überweisungsdetails — Fällig 7 Tage nach Check-out
  const defaultDue = (() => {
    const co = booking?.check_out;
    if (!co) return new Date().toISOString().slice(0,10);
    const d = new Date(co); d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0,10);
  })();
  const [dueDate, setDueDate] = useState(defaultDue);
  const extrasTotal = extras.filter(e => sel.has(e.id)).reduce((s, e) => s + (e.total || 0), 0);
  const grandTotal = (booking?.gross || 0) + extrasTotal;
  const defaultPayNote = `Wir bitten Sie, den Gesamtbetrag in Höhe von ${euro(grandTotal)} bis zum ${formatDate(dueDate)} auf folgendes Konto zu überweisen:`;
  const [payNote, setPayNote] = useState("");   // leer = Backend-Default-Template
  const [noteEdited, setNoteEdited] = useState(false);
  useEffect(() => { if (!noteEdited) setPayNote(defaultPayNote); }, [defaultPayNote, noteEdited]);

  const confirm = () => {
    const opts = {
      billing_name: billingName || null,
      billing_company: billingCompany || null,
      billing_attention: billingAttention || null,
      billing_address: billingAddress || null,
    };
    if (payMode === "transfer") {
      opts.paid_via_platform = false;
      opts.due_date = dueDate;
      opts.payment_note = payNote;
    } else {
      opts.paid_via_platform = true;
      opts.paid_platform_label = payMode === "booking" ? "Booking.com" : "Airbnb";
    }
    onConfirm([...sel], opts);
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="invoice-options-dialog">
        <DialogHeader>
          <DialogTitle>Rechnung erstellen — {booking?.booking_group_id ? `${(booking._room_names || [room.name]).filter(Boolean).join(" + ")} (${(booking._group_members?.length || 1)} Zimmer · 1 Rechnung)` : room.name} · {guestFullName}</DialogTitle>
        </DialogHeader>

        {/* Rechnungsempfänger */}
        <fieldset className="border border-border rounded-xl p-3 space-y-2.5">
          <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Rechnungsempfänger</legend>
          <div className="text-[11px] text-muted-foreground -mt-1">Optional — falls die Rechnung an eine Firma oder abweichende Anschrift geht.</div>
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <Label className="text-xs">Firma</Label>
              <Input value={billingCompany} onChange={e=>setBillingCompany(e.target.value)}
                     placeholder="z. B. Bosch Rexroth AG" className="rounded-lg mt-1" data-testid="inv-billing-company"/>
            </div>
            <div>
              <Label className="text-xs">z.Hd. (Ansprechpartner)</Label>
              <Input value={billingAttention} onChange={e=>setBillingAttention(e.target.value)}
                     placeholder="optional" className="rounded-lg mt-1" data-testid="inv-billing-attention"/>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Name auf Rechnung</Label>
              <Input value={billingName} onChange={e=>setBillingName(e.target.value)}
                     className="rounded-lg mt-1" data-testid="inv-billing-name"/>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Adresse (Straße, PLZ Ort, Land)</Label>
              <Input value={billingAddress} onChange={e=>setBillingAddress(e.target.value)}
                     className="rounded-lg mt-1" data-testid="inv-billing-address"/>
            </div>
          </div>
        </fieldset>

        {/* Zahlungsart */}
        <fieldset className="border border-border rounded-xl p-3 space-y-2.5">
          <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Zahlung</legend>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "transfer", label: "Überweisung", hint: "Text + Fälligkeit" },
              { v: "booking",  label: "Booking.com", hint: "bereits bezahlt" },
              { v: "airbnb",   label: "Airbnb",      hint: "bereits bezahlt" },
            ].map(o => (
              <button key={o.v} type="button" onClick={() => setPayMode(o.v)}
                      className={`text-left rounded-xl border p-2 ${payMode===o.v ? "bg-primary text-white border-primary" : "bg-white border-slate-300 hover:border-primary/60"}`}
                      data-testid={`inv-paymode-${o.v}`}>
                <div className="text-sm font-semibold">{o.label}</div>
                <div className={`text-[11px] ${payMode===o.v ? "text-white/85" : "text-muted-foreground"}`}>{o.hint}</div>
              </button>
            ))}
          </div>
          {payMode === "transfer" && (
            <div className="space-y-2 pt-1">
              <div>
                <Label className="text-xs">Fällig bis</Label>
                <Input type="date" value={dueDate} onChange={e=>setDueDate(e.target.value)}
                       className="rounded-lg mt-1 max-w-xs" data-testid="inv-due-date"/>
              </div>
              <div>
                <Label className="text-xs">Zahlungstext (auf der Rechnung — editierbar)</Label>
                <textarea value={payNote} onChange={e=>{ setPayNote(e.target.value); setNoteEdited(true); }}
                          rows={3}
                          className="w-full rounded-xl mt-1 border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid="inv-pay-note"/>
                <div className="text-[10px] text-muted-foreground mt-1">Kontoinhaber, IBAN, BIC & Verwendungszweck werden automatisch aus deinen Firmendaten unter dem Text angefügt.</div>
              </div>
            </div>
          )}
        </fieldset>

        {/* Extras */}
        {extras.length > 0 && (
          <fieldset className="border border-border rounded-xl p-3 space-y-1.5">
            <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Extras</legend>
            <div className="text-[11px] text-muted-foreground -mt-1">Trinkgeld erscheint nie auf der Rechnung.</div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {extras.map(e => (
                <label key={e.id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/40 cursor-pointer hover:bg-muted"
                       data-testid={`invoice-pick-${e.id}`}>
                  <Checkbox checked={sel.has(e.id)} onCheckedChange={() => toggle(e.id)} />
                  <div className="flex-1 text-sm">
                    <div className="font-medium">{e.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {e.qty > 1 ? `${e.qty} × ${euro(e.unit_price)} = ` : ""}<span className="font-semibold">{euro(e.total)}</span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {/* Summe */}
        <div className="border rounded-xl p-3 text-sm space-y-0.5 bg-slate-50">
          <div className="flex justify-between"><span className="text-muted-foreground">Übernachtung</span><span>{euro(booking?.gross || 0)}</span></div>
          {extrasTotal > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Extras gewählt</span><span>{euro(extrasTotal)}</span></div>}
          <div className="flex justify-between font-bold text-base pt-1 border-t"><span>Rechnung gesamt</span><span>{euro(grandTotal)}</span></div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} data-testid="invoice-picker-cancel" className="rounded-xl">Abbrechen</Button>
          <Button onClick={confirm} className="rounded-xl bg-primary" data-testid="invoice-picker-confirm">Rechnung erstellen</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Legacy-Name für externe Referenzen (falls irgendwo importiert)
const InvoiceExtrasPicker = InvoiceOptionsDialog;

/* --------- Blitz-Eingabe: alle Gast-Daten + Buchung in einem Zug --------- */
function safeAddDays(iso, days) {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return new Date(t + days * 86400000).toISOString().slice(0, 10);
}

function NotifyCheck({ checked, onChange, label, hint, testid }) {
  return (
    <label className="flex items-start gap-2 p-2 rounded-lg border border-border hover:bg-muted/30 cursor-pointer transition" data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="mt-0.5 rounded"/>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold">{label}</div>
        <div className="text-[10px] text-muted-foreground">{hint}</div>
      </div>
    </label>
  );
}

export function QuickBookingForm({ rooms, prefill, onSaved, onCancel }) {
  const todayIso = new Date().toISOString().slice(0,10);
  const nextDayIso = safeAddDays(todayIso, 1);
  // Prefill aus Zimmer-Verfügbarkeitssuche (nur wenn Datum wirklich gültig)
  const prefillCheckIn  = (prefill?.check_in && safeAddDays(prefill.check_in, 0)) || todayIso;
  const prefillCheckOut = prefill?.check_out
    ? prefill.check_out
    : safeAddDays(prefillCheckIn, Math.max(1, Number(prefill?.nights) || 1)) || nextDayIso;
  const prefillRoomIds  = (prefill?.room_ids && prefill.room_ids.length ? prefill.room_ids : (prefill?.room_id ? [prefill.room_id] : []));
  // Gast
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [email, setEmail]         = useState("");
  const [phone, setPhone]         = useState("");
  const [street, setStreet]       = useState("");
  const [postal, setPostal]       = useState("");
  const [city, setCity]           = useState("");
  const [country, setCountry]     = useState("Deutschland");
  const [guestNotes, setGuestNotes] = useState("");
  // Optionale Rechnungsadresse — wird dauerhaft am Gast gespeichert und bei jeder Rechnung vorbelegt
  const [billingCompany, setBillingCompany]     = useState("");
  const [billingAttention, setBillingAttention] = useState("");
  const [billingName, setBillingName]           = useState("");
  const [billingAddress, setBillingAddress]     = useState("");
  // Buchung – bei Mehrfach-Reservierung: Liste
  const [roomIds, setRoomIds]     = useState(prefillRoomIds);
  const [roomId, setRoomId]       = useState(prefillRoomIds[0] || rooms[0]?.id || "");
  const [checkIn, setCheckIn]     = useState(prefillCheckIn);
  const [checkOut, setCheckOut]   = useState(prefillCheckOut);
  const [persons, setPersons]     = useState(Math.max(1, Number(prefill?.guests) || 2));
  const [gross, setGross]         = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [codeApplied, setCodeApplied] = useState(null);
  const [source, setSource]       = useState("MSB");
  const [notifyConfirmation, setNotifyConfirmation] = useState(true);
  const [notifyCheckin, setNotifyCheckin]     = useState(true);
  const [notifyMeals, setNotifyMeals]         = useState(true);
  const [notifyReview, setNotifyReview]       = useState(true);
  const [notifyWhatsApp, setNotifyWhatsApp]   = useState(true);
  // Sprache für alle Auto-Nachrichten (Mail + WhatsApp)
  const [lang, setLang]                       = useState("de");
  // Granulare WhatsApp-Häkchen pro Buchung
  const [waConfirmation, setWaConfirmation]     = useState(true);
  const [waMealsUpsell, setWaMealsUpsell]       = useState(true);
  const [waNicIntro, setWaNicIntro]             = useState(true);
  const [waCheckin, setWaCheckin]               = useState(true);
  const [waMealsReminder, setWaMealsReminder]   = useState(true);
  const [waReview, setWaReview]                 = useState(true);
  const [breakfastQty, setBreakfastQty]       = useState(0);
  const [dinnerQty, setDinnerQty]             = useState(0);
  // Bei Mehr-Zimmer-Buchung: optional pro Zimmer aufteilen (überschreibt Gesamtwerte)
  const [perRoomMeals, setPerRoomMeals] = useState({}); // { [room_id]: { breakfast: n, dinner: n } }
  const [extrasPrices, setExtrasPrices]       = useState({ breakfast: 15, dinner: 15 });
  const [saving, setSaving]       = useState(false);
  const isMulti = roomIds.length > 1;
  // Gast-Autocomplete für Bestandsdaten
  const [suggestions, setSuggestions] = useState([]);
  const [matchedGuest, setMatchedGuest] = useState(null);

  useEffect(() => {
    if (!roomId && rooms[0]?.id) setRoomId(rooms[0].id);
  }, [rooms, roomId]);

  // Prefill synchronisieren, wenn Dialog aus Zimmer-Verfügbarkeitssuche
  // mit vorbelegten Werten geöffnet wird.
  useEffect(() => {
    if (!prefill) return;
    const ids = (prefill.room_ids && prefill.room_ids.length) ? prefill.room_ids : (prefill.room_id ? [prefill.room_id] : []);
    if (ids.length) { setRoomIds(ids); setRoomId(ids[0]); }
    if (prefill.check_in)  setCheckIn(prefill.check_in);
    if (prefill.check_out) setCheckOut(prefill.check_out);
    else if (prefill.check_in && prefill.nights) {
      const co = safeAddDays(prefill.check_in, prefill.nights || 1);
      if (co) setCheckOut(co);
    }
    if (prefill.guests) setPersons(Math.max(1, Number(prefill.guests) || 2));
    if (prefill.guest) {
      applyGuest(prefill.guest);
      if (/^(en|english|englisch)/i.test(prefill.guest.language || "")) setLang("en");
    }
  }, [prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live-Suche nach Bestandsgast anhand Nachname
  useEffect(() => {
    const q = lastName.trim();
    if (q.length < 2 || (matchedGuest && (matchedGuest.last_name || "").trim() === q)) { setSuggestions([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/guests?q=${encodeURIComponent(q)}`);
        if (!cancelled) setSuggestions((data || []).slice(0, 5));
      } catch { /* silent */ }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [lastName]);

  const applyGuest = (g) => {
    setFirstName(g.first_name || "");
    setLastName(g.last_name || "");
    setEmail(g.email || "");
    setPhone(g.phone || "");
    setStreet(g.street || "");
    setPostal(g.postal_code || "");
    setCity(g.city || "");
    setCountry(g.country || "Deutschland");
    setBillingCompany(g.billing_company || "");
    setBillingAttention(g.billing_attention || "");
    setBillingName(g.billing_name || "");
    setBillingAddress(g.billing_address || "");
    setStreet(g.street || "");
    setPostal(g.postal_code || "");
    setCity(g.city || "");
    setCountry(g.country || "Deutschland");
    setSuggestions([]);
    setMatchedGuest(g);
    toast.success(`Bestandsgast übernommen: ${g.first_name} ${g.last_name}`);
  };

  // Standardpreise für Extras laden (für Preisanzeige in Neuanlage)
  useEffect(() => {
    api.get("/settings/extras-prices").then(r => setExtrasPrices(r.data)).catch(() => {});
  }, []);

  const nights = (() => {
    if (!checkIn || !checkOut) return 0;
    const d = (new Date(checkOut) - new Date(checkIn)) / 86400000;
    return d > 0 ? Math.round(d) : 0;
  })();
  const room = rooms.find(r => r.id === roomId);
  const selectedRooms = roomIds.length ? roomIds.map(id => rooms.find(r => r.id === id)).filter(Boolean) : (room ? [room] : []);
  const suggestedGross = nights > 0 ? nights * selectedRooms.reduce((s, r) => s + (r.price_per_night || 0), 0) : 0;
  const applyCode = async () => {
    const base = gross === "" ? suggestedGross : Number(gross);
    if (!codeInput.trim() || !checkIn || !checkOut || !(base > 0)) { toast.error("Zeitraum und Betrag zuerst eintragen"); return; }
    try {
      const { data } = await api.post("/discount-codes/apply", { code: codeInput.trim(), check_in: checkIn, check_out: checkOut, gross: base });
      setGross(String(data.new_gross)); setCodeApplied(data);
      toast.success(`${data.label} – ${euro(data.discount)} abgezogen`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Code konnte nicht angewendet werden"); }
  };

  const submit = async () => {
    if (!firstName.trim()) { toast.error("Vorname eintragen"); return; }
    const ids = isMulti ? roomIds : [roomId];
    if (!ids.length || ids.some(x => !x)) { toast.error("Zimmer wählen"); return; }
    if (!checkIn || !checkOut || nights <= 0) { toast.error("Zeitraum prüfen (Abreise nach Anreise)"); return; }
    setSaving(true);
    try {
      // Betrag pro Zimmer: wenn User einen Gesamt-Betrag eingegeben hat, gleichmäßig aufteilen
      const perRoomGross = gross === "" ? null : (Number(gross) / ids.length);
      const payloadBase = {
        first_name: firstName.trim(),
        last_name:  lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        street: street.trim(),
        postal_code: postal.trim(),
        city: city.trim(),
        country: country.trim(),
        guest_notes: guestNotes.trim(),
        billing_company: billingCompany.trim(),
        billing_attention: billingAttention.trim(),
        billing_name: billingName.trim(),
        billing_address: billingAddress.trim(),
        check_in: checkIn,
        check_out: checkOut,
        guests: Number(persons) || 1,
        source,
        notify_confirmation: notifyConfirmation,
        notify_checkin: notifyCheckin,
        notify_meals: notifyMeals,
        notify_review: notifyReview,
        notify_whatsapp: notifyWhatsApp,
        lang,
        wa_confirmation: waConfirmation,
        wa_meals_upsell: waMealsUpsell,
        wa_nic_intro: waNicIntro,
        wa_checkin: waCheckin,
        wa_meals_reminder: waMealsReminder,
        wa_review: waReview,
      breakfast_qty: 0,
      dinner_qty: 0,
      };
      // Sequenziell buchen – nach erster Buchung findet der Backend-Guest-Matcher
      // dieselbe Person und legt keine Duplikate an.
      // Bei Mehrfach-Zimmer: alle Sub-Buchungen teilen sich EINE booking_group_id,
      // damit die Liste sie als 1 Buchung anzeigt.
      const groupId = ids.length > 1 ? (window.crypto?.randomUUID?.() || `grp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`) : null;
      // Bei Mehr-Zimmer-Buchungen: Auto-Confirmation-E-Mail auf Sub-Buchungen unterdrücken,
      // damit nicht N Mails rausgehen. Nach dem Loop rufen wir 1× den Gruppen-Endpoint auf,
      // der EINE Mail mit allen Zimmern verschickt.
      const suppressAutoConfirm = groupId ? { notify_confirmation: false } : {};
      // Frühstück/Abendessen pro Zimmer verteilen (Bug-Fix: früher wurden N Frühstücke
      // pro Sub-Buchung angelegt statt geteilt → Doppelzählung).
      const hasPerRoom = ids.length > 1 && Object.values(perRoomMeals).some(m => (m?.breakfast || m?.dinner));
      const totalBf = Number(breakfastQty) || 0;
      const totalDn = Number(dinnerQty) || 0;
      for (let i = 0; i < ids.length; i++) {
        const rid = ids[i];
        let bfForThis, dnForThis;
        if (hasPerRoom) {
          // Nutzer hat pro Zimmer verteilt
          bfForThis = Number(perRoomMeals[rid]?.breakfast || 0);
          dnForThis = Number(perRoomMeals[rid]?.dinner || 0);
        } else if (ids.length > 1) {
          // Kein Per-Zimmer-Split: Gesamt nur auf ERSTES Zimmer (Bug-Fix)
          bfForThis = i === 0 ? totalBf : 0;
          dnForThis = i === 0 ? totalDn : 0;
        } else {
          bfForThis = totalBf;
          dnForThis = totalDn;
        }
        await api.post("/bookings/quick", {
          ...payloadBase,
          ...suppressAutoConfirm,
          room_id: rid,
          gross: perRoomGross,
          discount_code: i === 0 && codeApplied ? codeApplied.code : null,
          booking_group_id: groupId,
          breakfast_qty: bfForThis,
          dinner_qty: dnForThis,
        });
      }
      // Gruppen-Bestätigung (E-Mail + WhatsApp 1×) auslösen, falls Toggle an
      if (groupId && notifyConfirmation) {
        try {
          await api.post(`/bookings/group/${groupId}/send-confirmation-email`);
        } catch (e) {
          // Wenn Gmail nicht verbunden oder kein E-Mail-Feld → nur loggen, Buchung ist trotzdem angelegt
          console.warn("Gruppen-Bestätigungsmail fehlgeschlagen:", e?.response?.data?.detail || e?.message);
        }
      }
      const roomsInfo = ids.length > 1 ? `${ids.length} Zimmer` : "Buchung";
      toast.success(matchedGuest
        ? `${roomsInfo} für ${firstName} ${lastName} angelegt`
        : `Neuer Gast + ${roomsInfo} angelegt`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Anlegen");
    } finally { setSaving(false); }
  };

  const onFormKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
  };

  return (
    <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1" onKeyDown={onFormKeyDown}>
      <div className="text-xs text-muted-foreground bg-primary/5 border border-primary/20 rounded-lg p-3">
        <strong>Ein Klick, alles gespeichert:</strong> Gast + Buchung werden gleichzeitig in der Datenbank angelegt.
        Existiert der Gast schon (Nachname eingeben), werden die Bestandsdaten übernommen und Notizen ergänzt.
        <span className="ml-1 opacity-70">⌘/Strg + Enter zum Speichern.</span>
      </div>

      {/* ────────── Gast ────────── */}
      <fieldset className="border border-border rounded-xl p-4 space-y-3">
        <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Gast</legend>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Vorname *</Label>
            <Input value={firstName} onChange={e=>setFirstName(e.target.value)}
                   autoFocus className="rounded-xl mt-1.5" data-testid="quick-firstname"/>
          </div>
          <div className="relative">
            <Label>Nachname</Label>
            <Input value={lastName} onChange={e=>{ setLastName(e.target.value); setMatchedGuest(null); }}
                   className="rounded-xl mt-1.5" data-testid="quick-lastname" autoComplete="off"/>
            {suggestions.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-border rounded-xl shadow-lg overflow-hidden">
                {suggestions.map(g => (
                  <button key={g.id} type="button" onClick={()=>applyGuest(g)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-primary/5 border-b border-border/30 last:border-b-0"
                          data-testid={`quick-guest-suggest-${g.id}`}>
                    <div className="font-medium">{g.first_name} {g.last_name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {g.email || g.phone || g.city || "Bestandsgast"}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {matchedGuest && (matchedGuest.notes || "").trim() && (
          <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="quick-existing-notes">
            <div className="font-semibold text-amber-900 mb-1">Bekannte Notizen zu diesem Gast:</div>
            <div className="whitespace-pre-line text-amber-900/80">{matchedGuest.notes}</div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>E-Mail</Label>
            <Input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                   className="rounded-xl mt-1.5" placeholder="gast@beispiel.de" data-testid="quick-email"/>
          </div>
          <div>
            <Label>Handy</Label>
            <Input value={phone} onChange={e=>setPhone(e.target.value)}
                   className="rounded-xl mt-1.5" placeholder="+49 176 ..." data-testid="quick-phone"/>
          </div>
        </div>
        <div>
          <Label>Straße & Hausnr.</Label>
          <Input value={street} onChange={e=>setStreet(e.target.value)}
                 className="rounded-xl mt-1.5" data-testid="quick-street"/>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>PLZ</Label>
            <Input value={postal} onChange={e=>setPostal(e.target.value)}
                   className="rounded-xl mt-1.5" data-testid="quick-postal"/>
          </div>
          <div>
            <Label>Stadt</Label>
            <Input value={city} onChange={e=>setCity(e.target.value)}
                   className="rounded-xl mt-1.5" data-testid="quick-city"/>
          </div>
          <div>
            <Label>Land</Label>
            <Input value={country} onChange={e=>setCountry(e.target.value)}
                   className="rounded-xl mt-1.5" data-testid="quick-country"/>
          </div>
        </div>
        <div>
          <Label>Interne Gast-Notizen <span className="text-[11px] font-normal text-muted-foreground">(dauerhaft am Gast gespeichert)</span></Label>
          <textarea value={guestNotes} onChange={e=>setGuestNotes(e.target.value)}
                    rows={2}
                    placeholder="z. B. Allergien, VIP, Frühaufsteher, Katze dabei …"
                    className="w-full rounded-xl mt-1.5 border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    data-testid="quick-guest-notes"/>
          <div className="text-[11px] text-muted-foreground mt-1">Wird bei allen zukünftigen Buchungen dieser Person automatisch angezeigt.</div>
        </div>

        {/* Rechnungsadresse (optional, dauerhaft am Gast) */}
        <fieldset className="border border-border rounded-xl p-3 space-y-2.5">
          <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
            Rechnungsadresse (optional)
          </legend>
          <div className="text-[11px] text-muted-foreground -mt-1">
            Nur ausfüllen, wenn die Rechnung an eine andere Firma/Adresse als der Gast geht.
            Wird dauerhaft am Gast gespeichert und bei jeder Rechnung automatisch übernommen.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Rechnungs-Firma</Label>
              <Input value={billingCompany} onChange={e=>setBillingCompany(e.target.value)}
                     placeholder="z. B. Bosch Rexroth AG" className="rounded-xl mt-1.5" data-testid="quick-billing-company"/>
            </div>
            <div>
              <Label>z.Hd. (Ansprechpartner)</Label>
              <Input value={billingAttention} onChange={e=>setBillingAttention(e.target.value)}
                     placeholder="optional" className="rounded-xl mt-1.5" data-testid="quick-billing-attention"/>
            </div>
            <div className="col-span-2">
              <Label>Name auf Rechnung</Label>
              <Input value={billingName} onChange={e=>setBillingName(e.target.value)}
                     placeholder="Leer = Vor- + Nachname von oben" className="rounded-xl mt-1.5" data-testid="quick-billing-name"/>
            </div>
            <div className="col-span-2">
              <Label>Rechnungs-Adresse (Straße, PLZ Ort, Land)</Label>
              <Input value={billingAddress} onChange={e=>setBillingAddress(e.target.value)}
                     className="rounded-xl mt-1.5" data-testid="quick-billing-address"/>
            </div>
          </div>
        </fieldset>
      </fieldset>

      {/* ────────── Buchung ────────── */}
      <fieldset className="border border-border rounded-xl p-4 space-y-3">
        <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Buchung</legend>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Zimmer * <span className="font-normal text-muted-foreground">(Mehrfachauswahl){roomIds.length ? ` · ${roomIds.length}` : ""}</span></Label>
            <div className="rounded-xl mt-1.5 border border-border px-2 py-2 bg-white flex flex-wrap gap-1.5" data-testid="quick-rooms-multi">
              {rooms.map(r => {
                const active = roomIds.includes(r.id);
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => {
                      const next = active
                        ? roomIds.filter(x => x !== r.id)
                        : [...roomIds, r.id];
                      setRoomIds(next);
                      setRoomId(next[0] || "");
                    }}
                    className={`h-8 px-3 rounded-full text-xs font-semibold border transition ${
                      active
                        ? "bg-primary text-white border-primary shadow-sm"
                        : "bg-white text-slate-700 border-slate-300 hover:border-primary/60"
                    }`}
                    data-testid={`quick-room-toggle-${r.id}`}
                  >
                    {active ? "✓ " : ""}{r.name}
                  </button>
                );
              })}
            </div>
            {isMulti && (
              <div className="text-[11px] text-muted-foreground mt-1">
                Gesamtbetrag wird gleichmäßig auf {roomIds.length} Zimmer verteilt.
              </div>
            )}
          </div>
          <div>
            <Label>Buchungsquelle *</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="quick-source-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="MSB">MSB (Direkt)</SelectItem>
                <SelectItem value="Booking.com">Booking.com</SelectItem>
                <SelectItem value="Airbnb">Airbnb</SelectItem>
                <SelectItem value="Website">Website</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Anreise</Label>
            <Input type="date" value={checkIn} onChange={e=>setCheckIn(e.target.value)}
                   className="rounded-xl mt-1.5" data-testid="quick-checkin"/>
          </div>
          <div>
            <Label>Abreise</Label>
            <Input type="date" value={checkOut} onChange={e=>setCheckOut(e.target.value)}
                   className="rounded-xl mt-1.5" data-testid="quick-checkout"/>
          </div>
          <div>
            <Label>Personen</Label>
            <Input type="number" min="1" max="10" value={persons} onChange={e=>setPersons(+e.target.value || 1)}
                   className="rounded-xl mt-1.5" data-testid="quick-persons"/>
          </div>
        </div>
        <div>
          <Label>Gesamtbetrag Brutto (€) *</Label>
          <Input type="number" step="0.01" value={gross} onChange={e=>setGross(e.target.value)}
                 placeholder="Kompletter Preis für den Gast"
                 className="rounded-xl mt-1.5" data-testid="quick-gross"/>
          <div className="text-xs text-muted-foreground mt-1">
            {nights > 0 ? `${nights} Nächte` : "Zeitraum wählen"}
          </div>
          <div className="flex gap-2 mt-2">
            <Input value={codeInput} onChange={e=>{ setCodeInput(e.target.value.toUpperCase()); setCodeApplied(null); }}
                   placeholder="Rabattcode (optional)" className="rounded-xl" data-testid="quick-discount-code"/>
            <Button type="button" variant="outline" className="rounded-xl" onClick={applyCode} data-testid="quick-discount-apply">Anwenden</Button>
          </div>
          {codeApplied && <div className="text-xs text-emerald-700 mt-1" data-testid="quick-discount-applied">{codeApplied.label}: − {euro(codeApplied.discount)} (Betrag oben bereits reduziert)</div>}
          {source === "Booking.com" && Number(gross) > 0 && (
            <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs space-y-0.5"
                 data-testid="commission-preview">
              <div className="flex justify-between text-amber-800">
                <span>Provision Booking.com (13,4 %):</span>
                <span className="font-semibold">− {euro(Number(gross) * 0.134)}</span>
              </div>
              <div className="flex justify-between text-emerald-700 font-bold border-t border-amber-200/60 pt-1 mt-1">
                <span>Netto-Umsatz für dich:</span>
                <span>{euro(Number(gross) * (1 - 0.134))}</span>
              </div>
            </div>
          )}
        </div>

        {/* ────────── Auto-Nachrichten an Gast ────────── */}
        <fieldset className="border border-border rounded-xl p-3 space-y-2">
          <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
            📧 Auto-Nachrichten an Gast
          </legend>
          <div className="text-[11px] text-muted-foreground mb-1">
            Nur ankreuzen was der Gast bekommen soll. Perfekt für Telefon- oder Vor-Ort-Buchungen.
          </div>

          {/* Sprache */}
          <div className="flex items-center gap-2 pb-2 border-b border-border/50">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Sprache:</span>
            <button type="button" onClick={() => setLang("de")}
                    data-testid="lang-de"
                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${lang === "de" ? "bg-primary text-white" : "bg-muted hover:bg-muted/70"}`}>
              🇩🇪 Deutsch
            </button>
            <button type="button" onClick={() => setLang("en")}
                    data-testid="lang-en"
                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${lang === "en" ? "bg-primary text-white" : "bg-muted hover:bg-muted/70"}`}>
              🇬🇧 English
            </button>
          </div>

          {/* E-Mail-Toggles */}
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground pt-1">✉️ E-Mail</div>
          <div className="grid grid-cols-2 gap-2">
            <NotifyCheck testid="notify-confirm" checked={notifyConfirmation} onChange={setNotifyConfirmation}
              label="Buchungsbestätigung" hint="direkt nach Anlage"/>
            <NotifyCheck testid="notify-checkin" checked={notifyCheckin} onChange={setNotifyCheckin}
              label="Check-in-Info" hint="Ankunftserinnerung + Formular"/>
            <NotifyCheck testid="notify-meals" checked={notifyMeals} onChange={setNotifyMeals}
              label="Mahlzeiten-Erinnerung" hint="Frühstück/Abendessen"/>
            <NotifyCheck testid="notify-review" checked={notifyReview} onChange={setNotifyReview}
              label="Bewertungsmail" hint="Danke-/Review-Mail nach Abreise"/>
          </div>

          {/* WhatsApp-Master + 6 Toggles */}
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground pt-2 flex items-center gap-2">
            <span>💬 WhatsApp</span>
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={notifyWhatsApp} onChange={e => setNotifyWhatsApp(e.target.checked)}
                     data-testid="notify-wa-master" className="rounded"/>
              <span className={notifyWhatsApp ? "text-primary" : "text-muted-foreground"}>Master an</span>
            </label>
          </div>
          <div className={`grid grid-cols-2 gap-2 ${notifyWhatsApp ? "" : "opacity-40 pointer-events-none"}`}>
            <NotifyCheck testid="wa-confirm" checked={waConfirmation} onChange={setWaConfirmation}
              label="1️⃣ Buchungsbestätigung" hint="sofort nach Anlage"/>
            <NotifyCheck testid="wa-meals-upsell" checked={waMealsUpsell} onChange={setWaMealsUpsell}
              label="2️⃣ Frühstück/Essen-Upsell" hint="~1 h nach Buchung"/>
            <NotifyCheck testid="wa-nic-intro" checked={waNicIntro} onChange={setWaNicIntro}
              label="3️⃣ NIC-Vorstellung" hint="am Abend nach Buchung"/>
            <NotifyCheck testid="wa-checkin" checked={waCheckin} onChange={setWaCheckin}
              label="4️⃣ Check-in-Erinnerung" hint="1 Tag vor Anreise"/>
            <NotifyCheck testid="wa-meals-reminder" checked={waMealsReminder} onChange={setWaMealsReminder}
              label="5️⃣ Mahlzeiten-Reminder" hint="18:00 am Vortag"/>
            <NotifyCheck testid="wa-review" checked={waReview} onChange={setWaReview}
              label="6️⃣ Bewertungs-Anfrage" hint="19:00 am Abreise-Tag"/>
          </div>
        </fieldset>

        {/* ────────── Extras direkt bei Anlage ────────── */}
        <fieldset className="border border-border rounded-xl p-3 space-y-2">
          <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
            🛒 Extras (optional)
          </legend>
          <div className="text-[11px] text-muted-foreground mb-1">
            Erspar dir Klicks später: Frühstück/Abendessen direkt mit erfassen. Getränke &amp; Trinkgeld kannst du später im 🛒-Körbchen ergänzen.
          </div>
          {isMulti && selectedRooms.length > 1 ? (
            <div className="space-y-2" data-testid="quick-meals-per-room">
              <div className="text-[11px] text-primary bg-primary/5 border border-primary/20 rounded-lg p-2">
                💡 <strong>Mehr-Zimmer-Buchung:</strong> Frühstück/Abendessen kannst du pro Zimmer verteilen (z.B. 2 auf Doppelzimmer, 1 auf Einzelzimmer). Wird sonst standardmäßig auf das erste Zimmer geschrieben.
              </div>
              {selectedRooms.map((r) => {
                const meals = perRoomMeals[r.id] || { breakfast: 0, dinner: 0 };
                return (
                  <div key={r.id} className="grid grid-cols-[1fr,80px,80px] gap-2 items-center bg-muted/30 rounded-lg p-2">
                    <div className="text-xs font-semibold truncate">{r.name}</div>
                    <div>
                      <Label className="text-[10px] text-amber-700">🥐 · {euro(extrasPrices.breakfast)}</Label>
                      <Input type="number" min="0" step="1" value={meals.breakfast}
                             onChange={e => setPerRoomMeals(p => ({ ...p, [r.id]: { ...(p[r.id]||{}), breakfast: e.target.value } }))}
                             className="rounded-lg h-8 text-sm text-right" data-testid={`quick-bf-room-${r.id}`} placeholder="0"/>
                    </div>
                    <div>
                      <Label className="text-[10px] text-red-700">🍽 · {euro(extrasPrices.dinner)}</Label>
                      <Input type="number" min="0" step="1" value={meals.dinner}
                             onChange={e => setPerRoomMeals(p => ({ ...p, [r.id]: { ...(p[r.id]||{}), dinner: e.target.value } }))}
                             className="rounded-lg h-8 text-sm text-right" data-testid={`quick-dn-room-${r.id}`} placeholder="0"/>
                    </div>
                  </div>
                );
              })}
              {(() => {
                const bf = Object.values(perRoomMeals).reduce((s, m) => s + (Number(m?.breakfast) || 0), 0);
                const dn = Object.values(perRoomMeals).reduce((s, m) => s + (Number(m?.dinner) || 0), 0);
                const total = bf * extrasPrices.breakfast + dn * extrasPrices.dinner;
                if (!bf && !dn) return null;
                return (
                  <div className="text-[11px] text-right pt-1 border-t border-border/50">
                    Gesamt Extras: <strong className="text-primary">{bf}× 🥐 + {dn}× 🍽 = {euro(total)}</strong>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">🥐 Frühstück · {euro(extrasPrices.breakfast)}/P</Label>
              <Input type="number" min="0" step="1" value={breakfastQty}
                     onChange={e => setBreakfastQty(e.target.value)}
                     className="rounded-xl mt-1 h-9" data-testid="quick-breakfast-qty" placeholder="0"/>
              {Number(breakfastQty) > 0 && (
                <div className="text-[10px] text-amber-700 mt-0.5">= {euro(Number(breakfastQty) * extrasPrices.breakfast)}</div>
              )}
            </div>
            <div>
              <Label className="text-xs">🍽️ Abendessen · {euro(extrasPrices.dinner)}/P</Label>
              <Input type="number" min="0" step="1" value={dinnerQty}
                     onChange={e => setDinnerQty(e.target.value)}
                     className="rounded-xl mt-1 h-9" data-testid="quick-dinner-qty" placeholder="0"/>
              {Number(dinnerQty) > 0 && (
                <div className="text-[10px] text-red-700 mt-0.5">= {euro(Number(dinnerQty) * extrasPrices.dinner)}</div>
              )}
            </div>
          </div>
          )}
        </fieldset>
      </fieldset>

      <div className="flex justify-end gap-2 sticky bottom-0 bg-background pt-3 border-t border-border">
        <Button variant="outline" onClick={onCancel} className="rounded-xl">Abbrechen</Button>
        <Button onClick={submit} disabled={saving} className="rounded-xl bg-primary" data-testid="quick-save-btn">
          {saving ? "Speichere…" : (matchedGuest ? "Gast aktualisieren + Buchung anlegen" : "Gast + Buchung anlegen")}
        </Button>
      </div>
    </div>
  );
}


// Sendeprotokoll-Panel: zeigt alle versandten Mails/Rechnungen/WhatsApps chronologisch
const MSG_KIND_META = {
  booking_confirmation:   { label: "Buchungsbestätigung", emoji: "✅", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  arrival_reminder:       { label: "Anreise-Erinnerung",  emoji: "🚗", color: "bg-blue-50 text-blue-800 border-blue-200" },
  checkin_form_reminder:  { label: "Check-in Formular",   emoji: "📝", color: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  meals_form_reminder:    { label: "Mahlzeiten-Reminder", emoji: "🍽️", color: "bg-amber-50 text-amber-800 border-amber-200" },
  checkout_thankyou:      { label: "Checkout-Danke",      emoji: "👋", color: "bg-slate-50 text-slate-800 border-slate-200" },
  invoice:                { label: "Rechnung",            emoji: "🧾", color: "bg-primary/10 text-primary border-primary/30" },
  whatsapp:               { label: "WhatsApp",            emoji: "💬", color: "bg-green-50 text-green-800 border-green-200" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function MessageLogPanel({ loading, log }) {
  const entries = log?.entries || [];
  return (
    <fieldset className="border border-border rounded-xl p-3 space-y-2" data-testid="booking-message-log">
      <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
        📤 Sendeprotokoll {entries.length > 0 && <span className="text-primary">({entries.length})</span>}
      </legend>
      {loading && <div className="text-sm text-muted-foreground py-2">Lade Protokoll…</div>}
      {!loading && entries.length === 0 && (
        <div className="text-sm text-muted-foreground py-3 text-center bg-muted/30 rounded-lg">
          Noch keine Nachrichten für diese Buchung versendet.
        </div>
      )}
      {!loading && entries.length > 0 && (
        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
          {entries.map((e, i) => {
            const kind = e.kind || (e.channel === "whatsapp" ? "whatsapp" : "booking_confirmation");
            const meta = MSG_KIND_META[kind] || { label: kind, emoji: "📧", color: "bg-slate-50 text-slate-800 border-slate-200" };
            const failed = e.status && e.status !== "success" && e.status !== "queued" && e.status !== "sent" && e.status !== "delivered";
            return (
              <div key={i} className={`rounded-lg border p-2.5 text-xs ${failed ? "bg-red-50 border-red-200" : meta.color}`}
                   data-testid={`msg-log-entry-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5 font-semibold">
                    <span className="text-base leading-none">{meta.emoji}</span>
                    <span>{meta.label}</span>
                    {e.channel === "whatsapp" && <span className="ml-1 text-[10px] uppercase tracking-wider opacity-70">WA</span>}
                    {kind === "invoice" && e.pdf_attached && (
                      <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold">📎 PDF</span>
                    )}
                    {kind === "invoice" && !e.pdf_attached && (
                      <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-semibold">⚠️ ohne PDF</span>
                    )}
                    {kind === "invoice" && e.manually_edited && (
                      <span className="ml-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">✏️ manuell</span>
                    )}
                  </div>
                  <div className="text-[11px] opacity-75 whitespace-nowrap">{fmtDateTime(e.sent_at)}</div>
                </div>
                {e.subject && (
                  <div className="mt-1 text-[11px] opacity-90 line-clamp-1"><strong>Betreff:</strong> {e.subject}</div>
                )}
                {e.to && (
                  <div className="text-[11px] opacity-75">
                    <strong>An:</strong> {e.to}
                    {e.cc && <><strong className="ml-2">CC:</strong> {e.cc}</>}
                    {e.sent_via && <span className="ml-2 opacity-60">via {e.sent_via}</span>}
                  </div>
                )}
                {e.rooms && (
                  <div className="text-[11px] opacity-75"><strong>Zimmer:</strong> {e.rooms}</div>
                )}
                {failed && e.error && (
                  <div className="text-[11px] mt-1 text-red-700"><strong>Fehler:</strong> {e.error.slice(0, 200)}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}

function BookingInvoicesPanel({ bookingId }) {
  const [items, setItems] = useState(null);
  const reload = () => api.get(`/bookings/${bookingId}/invoices`)
    .then(r => setItems(r.data?.invoices || []))
    .catch(() => setItems([]));
  useEffect(() => { let c=false; reload(); return ()=>{c=true;}; }, [bookingId]);

  const openPdf = (inv) => setPdfPreview({
    url: `${API}/invoices/${inv.id}/pdf?v=${Date.now()}`,
    title: `Rechnung ${inv.number}`,
    filename: `Rechnung_${inv.number}.pdf`,
  });
  const [pdfPreview, setPdfPreview] = useState(null);

  const statusColor = (s) => ({
    "Entwurf":   "bg-slate-100 text-slate-700",
    "erstellt":  "bg-blue-100 text-blue-800",
    "versendet": "bg-amber-100 text-amber-800",
    "bezahlt":   "bg-emerald-100 text-emerald-800",
    "storniert": "bg-red-100 text-red-800",
  }[s] || "bg-slate-100 text-slate-700");

  return (
    <fieldset className="border border-border rounded-xl p-3 space-y-2" data-testid="booking-invoices-panel">
      <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
        💶 Rechnungen {items && items.length > 0 && <span className="text-primary">({items.length})</span>}
      </legend>
      {items === null && <div className="text-sm text-muted-foreground py-2">Lade Rechnungen…</div>}
      {items !== null && items.length === 0 && (
        <div className="text-sm text-muted-foreground py-3 text-center bg-muted/30 rounded-lg">
          Noch keine Rechnung erstellt. (Beim Senden einer Buchungsbestätigung wird keine Rechnung erzeugt — nutze dafür den Rechnungs-Flow.)
        </div>
      )}
      {items !== null && items.length > 0 && (
        <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
          {items.map((inv, i) => (
            <div key={inv.id || i}
                 className="rounded-lg border border-border bg-white p-2.5 text-xs flex items-center justify-between gap-2"
                 data-testid={`booking-invoice-${inv.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 font-semibold">
                  <span>Rechnung {inv.number}</span>
                  <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${statusColor(inv.status)}`}>
                    {inv.status}
                  </span>
                </div>
                <div className="text-[11px] opacity-75 mt-0.5">
                  {formatDate(inv.invoice_date)} · <strong>{(inv.gross || 0).toFixed(2).replace(".",",")} €</strong>
                  {inv.sent_at && <> · gesendet {fmtDateTime(inv.sent_at)}</>}
                </div>
              </div>
              <Button size="sm" variant="outline"
                      className="rounded-lg h-8 px-2.5 text-xs flex-shrink-0"
                      onClick={() => openPdf(inv)}
                      data-testid={`open-invoice-pdf-${inv.id}`}>
                <FilePdf size={14} weight="duotone" className="mr-1"/>
                PDF
              </Button>
              <a href="/rechnungen" target="_blank" rel="noreferrer"
                 className="text-xs px-2 py-1.5 rounded border border-amber-300 text-amber-800 hover:bg-amber-50 inline-flex items-center gap-1 flex-shrink-0"
                 data-testid={`edit-invoice-link-${inv.id}`}
                 title="Zur Rechnungsliste wechseln">
                ✎ Ändern
              </a>
            </div>
          ))}
        </div>
      )}
      <PdfViewerDialog
        open={!!pdfPreview}
        onClose={() => setPdfPreview(null)}
        url={pdfPreview?.url}
        title={pdfPreview?.title}
        filename={pdfPreview?.filename}
      />
    </fieldset>
  );
}


function BookingFormsPanel({ bookingId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get(`/bookings/${bookingId}/forms`)
      .then(r => { if (!cancel) setItems(r.data?.items || []); })
      .catch(() => { if (!cancel) setItems([]); });
    return () => { cancel = true; };
  }, [bookingId]);

  const openPdf = (form) => {
    const path = form.source === "guest_forms"
      ? `/guest-forms/${form.id}/pdf`
      : `/forms/submissions/${form.id}/pdf`;
    setPdfPreview({
      url: `${API}${path}`,
      title: `${form.label || "Formular"}${form.guest_name ? ` — ${form.guest_name}` : ""}`,
      filename: `${(form.type || "formular")}_${form.id}.pdf`,
    });
  };
  const [pdfPreview, setPdfPreview] = useState(null);

  return (
    <fieldset className="border border-border rounded-xl p-3 space-y-2" data-testid="booking-forms-panel">
      <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
        📋 Eingereichte Formulare {items && items.length > 0 && <span className="text-primary">({items.length})</span>}
      </legend>
      {items === null && <div className="text-sm text-muted-foreground py-2">Lade Formulare…</div>}
      {items !== null && items.length === 0 && (
        <div className="text-sm text-muted-foreground py-3 text-center bg-muted/30 rounded-lg">
          Noch keine Gast-Formulare eingereicht (Check-in, Frühstück, Essen).
        </div>
      )}
      {items !== null && items.length > 0 && (
        <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
          {items.map((f, i) => (
            <div key={f.id || i}
                 className="rounded-lg border border-border bg-white p-2.5 text-xs flex items-start justify-between gap-2"
                 data-testid={`booking-form-${f.type}-${i}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 font-semibold">
                  <span className="text-base leading-none">{f.emoji}</span>
                  <span>{f.label}</span>
                  {f.meal_type && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 ml-1">{f.meal_type}</span>}
                </div>
                <div className="text-[11px] opacity-75 mt-0.5">
                  {f.guest_name && <><strong>Gast:</strong> {f.guest_name} · </>}
                  {f.room_name && <><strong>Zimmer:</strong> {f.room_name} · </>}
                  {f.date && <><strong>am:</strong> {f.date} {f.time}</>}
                  {!f.date && f.submitted_at && <>eingereicht: {fmtDateTime(f.submitted_at)}</>}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="rounded-lg h-8 px-2.5 text-xs flex-shrink-0"
                onClick={() => openPdf(f)}
                data-testid={`open-form-pdf-${f.type}-${i}`}
              >
                <FilePdf size={14} weight="duotone" className="mr-1"/>
                PDF
              </Button>
            </div>
          ))}
        </div>
      )}
      <PdfViewerDialog
        open={!!pdfPreview}
        onClose={() => setPdfPreview(null)}
        url={pdfPreview?.url}
        title={pdfPreview?.title}
        filename={pdfPreview?.filename}
      />
    </fieldset>
  );
}


function BookingEditDialog({ booking, onClose, onSaved }) {
  const [firstName, setFirstName] = useState(booking.guest?.first_name || "");
  const [lastName, setLastName] = useState(booking.guest?.last_name || "");
  const [company, setCompany] = useState(booking.guest?.company || "");
  const [checkIn, setCheckIn] = useState(booking.check_in || "");
  const [checkOut, setCheckOut] = useState(booking.check_out || "");
  const [gross, setGross] = useState(booking.gross ?? 0);
  const [status, setStatus] = useState(booking.status || "Bestätigt");
  const [payment, setPayment] = useState(booking.payment_status || "Offen");
  const [source, setSource] = useState(booking.source || "MSB");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [msgLog, setMsgLog] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  // Mehr-Zimmer-Buchung: alle Gruppenmitglieder mit Einzel-Preis (editierbar)
  const [groupMembers, setGroupMembers] = useState(null);  // null=laden, []=solo, [...]=multi
  const [memberGrosses, setMemberGrosses] = useState({});  // { bookingId: grossValue }

  useEffect(() => {
    let cancel = false;
    setLogLoading(true);
    Promise.all([
      api.get(`/bookings/${booking.id}/message-log`),
      api.get(`/bookings/${booking.id}/group-details`),
    ])
      .then(([logR, grpR]) => {
        if (cancel) return;
        setMsgLog(logR.data);
        const members = grpR.data?.members || [];
        setGroupMembers(members);
        if (members.length > 1) {
          const map = {};
          members.forEach(m => { map[m.id] = m.gross; });
          setMemberGrosses(map);
        }
      })
      .catch(() => { if (!cancel) { setMsgLog({ entries: [] }); setGroupMembers([]); } })
      .finally(() => { if (!cancel) setLogLoading(false); });
    return () => { cancel = true; };
  }, [booking.id]);

  const isMultiRoom = (groupMembers?.length || 0) > 1;
  const groupTotal = isMultiRoom
    ? Object.values(memberGrosses).reduce((s, v) => s + (parseFloat(v) || 0), 0)
    : parseFloat(gross) || 0;

  const save = async () => {
    if (!firstName.trim() || !checkIn || !checkOut) {
      toast.error("Vorname, Anreise und Abreise sind Pflicht");
      return;
    }
    if (new Date(checkOut) <= new Date(checkIn)) {
      toast.error("Abreise muss nach Anreise liegen");
      return;
    }
    setSaving(true);
    try {
      // Mehr-Zimmer: pro Sub-Buchung separat updaten (jede mit eigenem Preis)
      if (isMultiRoom) {
        for (const m of groupMembers) {
          const val = parseFloat(memberGrosses[m.id]);
          await api.patch(`/bookings/${m.id}/edit`, {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            company: company.trim(),
            check_in: checkIn,
            check_out: checkOut,
            gross: isNaN(val) ? m.gross : val,
            status,
            payment_status: payment,
            source,
          });
        }
      } else {
        await api.patch(`/bookings/${booking.id}/edit`, {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          company: company.trim(),
          check_in: checkIn,
          check_out: checkOut,
          gross: +gross,
          status,
          payment_status: payment,
          source,
        });
      }
      toast.success(isMultiRoom
        ? `${groupMembers.length} Zimmer der Buchung aktualisiert · Gesamt ${euro(groupTotal)}`
        : "Änderungen gespeichert");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  const deleteBooking = async () => {
    const guestName = `${firstName} ${lastName}`.trim() || "diese Buchung";
    if (!window.confirm(
      `⚠️ Möchten Sie ${guestName} (${checkIn} → ${checkOut}) wirklich KOMPLETT löschen?\n\n` +
      "Die Buchung wird endgültig aus der Datenbank entfernt."
    )) return;
    setDeleting(true);
    try {
      await api.delete(`/bookings/${booking.id}`);
      toast.success("Buchung gelöscht");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally { setDeleting(false); }
  };

  return (
    <Dialog open onOpenChange={(v)=>{ if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-booking-dialog">
        <DialogHeader>
          <DialogTitle>Buchung bearbeiten</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <GuestAccessPanel bookingId={booking.id} />
          <div className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-3">
            Alle Felder werden sofort in der Datenbank gespeichert. Provision bleibt unberührt.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Vorname</Label>
              <Input value={firstName} onChange={e=>setFirstName(e.target.value)}
                     className="rounded-xl mt-1.5" data-testid="edit-first-name"/>
            </div>
            <div>
              <Label>Nachname</Label>
              <Input value={lastName} onChange={e=>setLastName(e.target.value)}
                     className="rounded-xl mt-1.5" data-testid="edit-last-name"/>
            </div>
          </div>

          <div>
            <Label>Firma (optional)</Label>
            <Input value={company} onChange={e=>setCompany(e.target.value)}
                   className="rounded-xl mt-1.5" data-testid="edit-company"/>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Anreise</Label>
              <Input type="date" value={checkIn} onChange={e=>setCheckIn(e.target.value)}
                     className="rounded-xl mt-1.5" data-testid="edit-checkin"/>
            </div>
            <div>
              <Label>Abreise</Label>
              <Input type="date" value={checkOut} onChange={e=>setCheckOut(e.target.value)}
                     className="rounded-xl mt-1.5" data-testid="edit-checkout"/>
            </div>
          </div>

          <div>
            {isMultiRoom ? (
              <fieldset className="border border-primary/30 rounded-xl p-3 space-y-2" data-testid="multi-room-editor">
                <legend className="px-2 text-[11px] uppercase tracking-widest text-primary font-semibold">
                  🏨 Mehr-Zimmer-Buchung · {groupMembers.length} Zimmer
                </legend>
                {groupMembers.map((m, i) => (
                  <div key={m.id} className="flex items-center gap-3 rounded-lg bg-primary/5 border border-primary/20 p-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{m.room_name}</div>
                      <div className="text-[11px] text-muted-foreground">{m.nights} Nächte</div>
                    </div>
                    <div className="w-32">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Brutto €</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={memberGrosses[m.id] ?? ""}
                        onChange={e => setMemberGrosses(prev => ({ ...prev, [m.id]: e.target.value }))}
                        className="rounded-lg h-9 text-right font-semibold"
                        data-testid={`edit-member-gross-${i}`}
                      />
                    </div>
                  </div>
                ))}
                <div className="flex justify-end items-center gap-2 pt-1 border-t border-primary/20">
                  <span className="text-xs text-muted-foreground">Gesamt-Brutto</span>
                  <span className="text-base font-bold text-primary" data-testid="multi-room-total">
                    {euro(groupTotal)}
                  </span>
                </div>
              </fieldset>
            ) : (
              <>
                <Label>Betrag (Brutto in €)</Label>
                <Input type="number" step="0.01" value={gross} onChange={e=>setGross(e.target.value)}
                       className="rounded-xl mt-1.5" data-testid="edit-gross"/>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="edit-status"><SelectValue/></SelectTrigger>
                <SelectContent>{["Angefragt","Bestätigt","Anreise bald","Aktuell im Haus","Abgereist","Storniert","Privat gesperrt"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Zahlung</Label>
              <Select value={payment} onValueChange={setPayment}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="edit-payment"><SelectValue/></SelectTrigger>
                <SelectContent>{PAY_OPTIONS.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Buchungsquelle</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="edit-source"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="MSB">🏡 MSB (Direkt / Privat)</SelectItem>
                <SelectItem value="Booking.com">🅱️ Booking.com</SelectItem>
                <SelectItem value="Airbnb">🅰️ Airbnb</SelectItem>
                <SelectItem value="Website">🌐 Website</SelectItem>
              </SelectContent>
            </Select>
            {source !== (booking.source || "MSB") && (
              <p className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5 mt-1.5 leading-snug" data-testid="edit-source-hint">
                ℹ️ Du änderst die Quelle von <b>{booking.source || "MSB"}</b> → <b>{source}</b>.
                {source === "Booking.com" && " Die Provision (13,4 %) wird automatisch neu berechnet. ✓"}
                {source === "MSB" && " Eine evtl. gesetzte Booking.com-Provision wird automatisch entfernt. ✓"}
                {source === "Airbnb" && " Die Provision wird automatisch entfernt (bei Airbnb wird der Nettobetrag ausgezahlt)."}
              </p>
            )}
          </div>

          <MessageLogPanel loading={logLoading} log={msgLog} />
          <BookingInvoicesPanel bookingId={booking.id} />
          <BookingFormsPanel bookingId={booking.id} />
        </div>
        <div className="flex justify-between gap-2 mt-2">
          <Button variant="outline" onClick={deleteBooking} disabled={saving || deleting}
                  className="rounded-xl border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  data-testid="edit-delete-booking">
            🗑️ {deleting ? "Lösche…" : "Buchung komplett löschen"}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-xl">Abbrechen</Button>
            <Button onClick={save} disabled={saving} className="rounded-xl bg-primary" data-testid="edit-save">
              {saving ? "Speichere…" : "Speichern"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function CommissionDialog({ booking, onClose, onSaved }) {
  const [base, setBase] = useState(booking.commissionable_amount ?? "");
  const [fee, setFee] = useState(booking.commission_amount ?? "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (base === "" || fee === "") {
      toast.error("Bitte beide Beträge exakt aus dem Beleg eintragen");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/bookings/${booking.id}/commission`, {
        commissionable_amount: +base,
        commission_amount_exact: +fee,
        reason,
      });
      toast.success("Provision exakt erfasst");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v)=>{ if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="commission-dialog">
        <DialogHeader>
          <DialogTitle>Provision aus Booking-Beleg erfassen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="msb-card bg-muted/30 text-sm">
            <div className="font-semibold">{booking.guest?.first_name} {booking.guest?.last_name}</div>
            <div className="text-xs text-muted-foreground">
              {booking.room?.name} · {formatDate(booking.check_in)} → {formatDate(booking.check_out)} · Brutto {euro(booking.gross)}
            </div>
          </div>
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
            Bitte nur die EXAKTEN Werte aus dem Booking.com-Beleg eintragen. Keine Schätzung.
          </div>
          <div>
            <Label>Kommissionsfähiger Betrag (€)</Label>
            <Input type="number" step="0.01" value={base} onChange={e=>setBase(e.target.value)}
                   className="rounded-xl mt-1.5" data-testid="comm-base"/>
          </div>
          <div>
            <Label>Kommission und Gebühren (€)</Label>
            <Input type="number" step="0.01" value={fee} onChange={e=>setFee(e.target.value)}
                   className="rounded-xl mt-1.5" data-testid="comm-fee"/>
          </div>
          <div>
            <Label>Notiz (optional)</Label>
            <Input value={reason} onChange={e=>setReason(e.target.value)} placeholder="z. B. Belegnummer"
                   className="rounded-xl mt-1.5"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl">Abbrechen</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-primary" data-testid="comm-save">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AmountEditDialog({ booking, onClose, onSaved }) {
  const [gross, setGross] = useState(booking.gross || 0);
  const [ppn, setPpn] = useState(booking.price_per_night || 0);
  const [payStatus, setPayStatus] = useState(booking.payment_status || "offen");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const nights = booking.nights || 1;

  const onGrossChange = (v) => {
    setGross(v);
    if (nights > 0) setPpn(+(v / nights).toFixed(2));
  };
  const onPpnChange = (v) => {
    setPpn(v);
    setGross(+(v * nights).toFixed(2));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/bookings/${booking.id}/amount`, {
        gross: +gross,
        payment_status: payStatus,
        reason,
      });
      toast.success("Betrag korrigiert");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v)=>{ if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="edit-amount-dialog">
        <DialogHeader>
          <DialogTitle>Buchungsbetrag korrigieren</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="msb-card bg-muted/30 text-sm">
            <div className="font-semibold">{booking.guest?.first_name} {booking.guest?.last_name}</div>
            <div className="text-xs text-muted-foreground">
              {booking.room?.name} · {formatDate(booking.check_in)} → {formatDate(booking.check_out)} · {nights} Nächte
            </div>
            <div className="text-xs text-muted-foreground mt-1">Aktuell: {euro(booking.gross)} brutto · {euro(booking.price_per_night)}/Nacht</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Neuer Bruttobetrag (€)</Label>
              <Input type="number" step="0.01" value={gross} onChange={e=>onGrossChange(+e.target.value)} className="rounded-xl mt-1.5" data-testid="edit-amount-gross"/>
            </div>
            <div>
              <Label>Preis / Nacht (€)</Label>
              <Input type="number" step="0.01" value={ppn} onChange={e=>onPpnChange(+e.target.value)} className="rounded-xl mt-1.5" data-testid="edit-amount-ppn"/>
            </div>
          </div>

          <div>
            <Label>Zahlungsstatus</Label>
            <Select value={payStatus} onValueChange={setPayStatus}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="edit-amount-paystatus"><SelectValue/></SelectTrigger>
              <SelectContent>{["offen","Anzahlung","bezahlt","über Booking.com bezahlt","Kostenübernahme"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label>Grund / Notiz (optional)</Label>
            <Input value={reason} onChange={e=>setReason(e.target.value)} placeholder="z. B. Excel-Import-Korrektur"
                   className="rounded-xl mt-1.5" data-testid="edit-amount-reason"/>
          </div>

          <div className="text-xs text-muted-foreground">Netto und Provision werden automatisch neu berechnet.</div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl">Abbrechen</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-primary" data-testid="edit-amount-save">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
